import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, FilePlus2, FileUp, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  CLIENT_FIRMS,
  FEE_SLIP_STATUSES,
  addFeeSlip,
  isPdf,
  readClerkStatement,
  saveClerkStatement,
  statementFirmMismatch,
  statementRecord,
  statementTotal,
  uploadTooLarge,
} from "@/lib/clerkUploads";
import { FEE_SLIP_CLERK_CHOICES, clerkDisplayName } from "@/lib/clerkDirectory";

const money = (value) => `$${Number(value || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fieldClass = "mt-1.5 text-xs";

// Upload a clerk's statement: read it, check what was read, then save it to the firm's
// clerk statements. Administrators only (the page shows it in admin mode).
export function ClerkStatementUploadCard({ brand, headerClass, buttonClass, onSaved }) {
  const inputRef = useRef(null);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statement, setStatement] = useState(null);
  const firm = CLIENT_FIRMS[brand];
  const mismatch = statement ? statementFirmMismatch(statement, brand) : "";

  const choose = async (file) => {
    if (!file) return;
    if (!isPdf(file)) return toast.error("Choose the clerk's statement as a PDF.");
    if (uploadTooLarge(file)) return toast.error("That PDF is over 3 MB. Save a smaller copy and try again.");
    setReading(true);
    setStatement(null);
    try {
      const read = await readClerkStatement(file);
      if (!read.rows.length) toast.warning("No invoices could be read from this statement. Check it is the clerk's statement PDF.");
      setStatement(read);
    } catch (error) {
      toast.error(error.message || "The statement could not be read.");
    } finally {
      setReading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveClerkStatement(statementRecord(statement, brand, statement.filename));
      toast.success(`${clerkDisplayName(statement.clerk_name || statement.firm)} statement saved for ${firm.label}.`);
      setStatement(null);
      onSaved?.();
    } catch (error) {
      toast.error(error.message || "The statement could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  if (!firm) return null;
  return (
    <Card className="shadow-sm">
      <CardHeader className={`bg-gradient-to-r ${headerClass} text-white rounded-t-lg py-4`}>
        <CardTitle className="text-base font-semibold flex items-center gap-2"><FileUp className="w-5 h-5" /> Upload a clerk statement</CardTitle>
        <CardDescription className="text-white/80 text-xs">A clerk's statement of fees owed. Its invoices appear under Clerk Statements for {firm.label}, and {firm.label} see them too.</CardDescription>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(event) => choose(event.target.files?.[0])} />
        {!statement && (
          <Button type="button" onClick={() => inputRef.current?.click()} disabled={reading} className={`w-full text-white text-xs ${buttonClass}`}>
            {reading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Reading the statement…</> : "Choose statement PDF"}
          </Button>
        )}
        {statement && (
          <div className="space-y-3 text-xs">
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              <dt className="text-slate-500">Clerk</dt><dd className="font-semibold text-slate-800" title={statement.clerk_name || statement.firm || ""}>{clerkDisplayName(statement.clerk_name || statement.firm) || "Not read"}</dd>
              <dt className="text-slate-500">As of</dt><dd className="font-semibold text-slate-800">{statement.as_of || "Not read"}</dd>
              <dt className="text-slate-500">Invoices</dt><dd className="font-semibold text-slate-800">{statement.rows.length}</dd>
              <dt className="text-slate-500">Outstanding</dt><dd className="font-semibold text-slate-800">{money(statementTotal(statement.rows))}</dd>
              <dt className="text-slate-500">File</dt><dd className="truncate text-slate-700" title={statement.filename}>{statement.filename}</dd>
            </dl>
            {statement.rows.length > 0 && (
              <ul className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {statement.rows.map((row, index) => (
                  <li key={`${row.invoice_number}-${index}`} className="flex justify-between gap-2 px-2.5 py-1.5">
                    <span className="truncate">{row.client_name || "Client not read"} <span className="text-slate-400">· {row.invoice_number || "no number"}</span></span>
                    <span className="flex-shrink-0 font-semibold">{row.outstanding_amount || "—"}</span>
                  </li>
                ))}
              </ul>
            )}
            {statement.firm_total != null && Math.abs(Number(statement.firm_total) - statementTotal(statement.rows)) >= 0.01 && (
              <p role="alert" className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 font-semibold text-amber-900">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                The statement says {money(statement.firm_total)} is outstanding, but the invoices read add up to {money(statementTotal(statement.rows))}. Some may have been missed: check against the PDF before saving.
              </p>
            )}
            {mismatch && (
              <p role="alert" className="flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 font-semibold text-amber-900">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                This statement names {mismatch}, but it will be saved for {firm.label}. Check you are on the right firm's page.
              </p>
            )}
            {!(statement.clerk_name || statement.firm) && (
              <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 font-semibold text-red-800">The clerk could not be read from this statement, so it can't be saved. Check it is the clerk's statement PDF.</p>
            )}
            <div className="flex gap-2">
              <Button type="button" onClick={save} disabled={saving || !statement.rows.length || !(statement.clerk_name || statement.firm)} className={`flex-1 text-white text-xs ${buttonClass}`}>
                {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving…</> : `Save for ${firm.label}`}
              </Button>
              <Button type="button" variant="outline" onClick={() => setStatement(null)} disabled={saving} className="text-xs">Cancel</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const emptyFeeSlip = { clientName: "", clerkName: "", counselName: "", amount: "", invoiceNumber: "", status: "not_paid", notes: "" };

// Add one counsel fee slip by hand, for a slip that did not arrive by email.
export function AddFeeSlipCard({ apiBase, brand, headerClass, buttonClass, onSaved }) {
  const inputRef = useRef(null);
  const [form, setForm] = useState(emptyFeeSlip);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const firm = CLIENT_FIRMS[brand];
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await addFeeSlip(apiBase, { ...form, brand, file });
      toast.success(`Fee slip added for ${form.clientName.trim()}.`);
      setForm(emptyFeeSlip);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      onSaved?.();
    } catch (error) {
      toast.error(error.message || "The fee slip could not be added.");
    } finally {
      setSaving(false);
    }
  };

  if (!firm) return null;
  return (
    <Card className="shadow-sm">
      <CardHeader className={`bg-gradient-to-r ${headerClass} text-white rounded-t-lg py-4`}>
        <CardTitle className="text-base font-semibold flex items-center gap-2"><FilePlus2 className="w-5 h-5" /> Add a fee slip</CardTitle>
        <CardDescription className="text-white/80 text-xs">One counsel fee slip that didn't arrive by email. It appears under Fee Slips for {firm.label}.</CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="add-fee-slip-client" className="text-xs font-semibold text-slate-700">Client</Label>
            <Input id="add-fee-slip-client" value={form.clientName} onChange={set("clientName")} required className={fieldClass} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="add-fee-slip-clerk" className="text-xs font-semibold text-slate-700">Clerk list</Label>
              <select id="add-fee-slip-clerk" value={form.clerkName} onChange={set("clerkName")} required className="mt-1.5 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs">
                <option value="">Choose…</option>
                {FEE_SLIP_CLERK_CHOICES.map((clerk) => <option key={clerk.code} value={clerk.code}>{clerk.name}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="add-fee-slip-counsel" className="text-xs font-semibold text-slate-700">Counsel</Label>
              <Input id="add-fee-slip-counsel" value={form.counselName} onChange={set("counselName")} className={fieldClass} />
            </div>
            <div>
              <Label htmlFor="add-fee-slip-amount" className="text-xs font-semibold text-slate-700">Amount ($)</Label>
              <Input id="add-fee-slip-amount" value={form.amount} onChange={set("amount")} inputMode="decimal" className={fieldClass} />
            </div>
            <div>
              <Label htmlFor="add-fee-slip-invoice" className="text-xs font-semibold text-slate-700">Invoice no.</Label>
              <Input id="add-fee-slip-invoice" value={form.invoiceNumber} onChange={set("invoiceNumber")} className={fieldClass} />
            </div>
          </div>
          <div>
            <Label htmlFor="add-fee-slip-status" className="text-xs font-semibold text-slate-700">Status</Label>
            <select id="add-fee-slip-status" value={form.status} onChange={set("status")} className="mt-1.5 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-xs">
              {FEE_SLIP_STATUSES.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="add-fee-slip-notes" className="text-xs font-semibold text-slate-700">Notes</Label>
            <Input id="add-fee-slip-notes" value={form.notes} onChange={set("notes")} className={fieldClass} />
          </div>
          <div>
            <Label htmlFor="add-fee-slip-file" className="text-xs font-semibold text-slate-700">Fee slip PDF</Label>
            <input id="add-fee-slip-file" ref={inputRef} type="file" accept="application/pdf,.pdf" onChange={(event) => setFile(event.target.files?.[0] || null)} required className="mt-1.5 block w-full text-xs" />
          </div>
          <Button type="submit" disabled={saving} className={`w-full text-white text-xs ${buttonClass}`}>
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Adding…</> : "Add fee slip"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
